import { supabase } from './supabaseClient';
import { ServiceAdvisorService } from './serviceAdvisorService';

const TABLE = 'mechanic_assignment_cities';

function normalizeCity(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function tokensMatch(a, b) {
  const na = normalizeCity(a);
  const nb = normalizeCity(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  return na.includes(nb) || nb.includes(na);
}

function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  } catch {
    return {};
  }
}

export function clampBellSeconds(value, fallback = 1800) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(1800, Math.max(1800, Math.round(n)));
}

export class MechanicAssignmentCityService {
  static async listAll() {
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  }

  static async listActive() {
    const { data, error } = await supabase
      .from(TABLE)
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true })
      .order('name', { ascending: true });
    if (error) throw error;
    return data || [];
  }

  static matchCity(input, cities = []) {
    if (!input?.trim() || !cities.length) return null;
    const normalized = normalizeCity(input);

    const exact = cities.find((c) => normalizeCity(c.name) === normalized);
    if (exact) return exact;

    const districtHit = cities.find((c) => tokensMatch(c.district, input));
    if (districtHit) return districtHit;

    const aliasHit = cities.find((c) =>
      (c.aliases || []).some((alias) => tokensMatch(alias, input)),
    );
    if (aliasHit) return aliasHit;

    return cities.find((c) => tokensMatch(c.name, input)) || null;
  }

  static async create(payload) {
    const row = {
      name: String(payload.name || '').trim(),
      aliases: Array.isArray(payload.aliases)
        ? payload.aliases
        : String(payload.aliases || '')
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean),
      district: String(payload.district || '').trim() || null,
      state: String(payload.state || '').trim() || null,
      sort_order: Number(payload.sort_order) || 0,
      is_active: payload.is_active !== false,
      notes: String(payload.notes || '').trim() || null,
      bell_seconds: clampBellSeconds(payload.bell_seconds),
    };
    if (!row.name) throw new Error('City / district name is required');
    const { data, error } = await supabase.from(TABLE).insert([row]).select().single();
    if (error) throw error;
    return data;
  }

  static async update(id, updates) {
    const row = {
      ...updates,
      updated_at: new Date().toISOString(),
    };
    if (typeof row.aliases === 'string') {
      row.aliases = row.aliases
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
    if (row.name != null) row.name = String(row.name).trim();
    if (row.bell_seconds != null) row.bell_seconds = clampBellSeconds(row.bell_seconds);
    const { data, error } = await supabase.from(TABLE).update(row).eq('id', id).select().single();
    if (error) throw error;
    return data;
  }

  static async stampPartnerBellOnRequest(requestId, bellSeconds) {
    const seconds = clampBellSeconds(bellSeconds);
    const { error } = await supabase
      .from('service_requests')
      .update({
        partner_bell_seconds: seconds,
        partner_bell_started_at: new Date().toISOString(),
      })
      .eq('id', requestId);
    if (error) throw error;
    return seconds;
  }

  static async remove(id) {
    const { error } = await supabase.from(TABLE).delete().eq('id', id);
    if (error) throw error;
  }

  /** Search mechanics for a configured city / district (text + available pool). */
  static async searchMechanicsForCity(cityRow, serviceType = null) {
    const queries = [
      cityRow?.name,
      cityRow?.district,
      ...((cityRow?.aliases || []).slice(0, 3) || []),
    ]
      .map((q) => String(q || '').trim())
      .filter(Boolean);

    const byId = new Map();

    for (const q of queries) {
      const textRes = await ServiceAdvisorService.searchMechanics(q, serviceType);
      if (textRes.success) {
        for (const m of textRes.data || []) {
          if (m?.id != null) byId.set(String(m.id), m);
        }
      }
      const availRes = await ServiceAdvisorService.getAvailableMechanics(serviceType, q, null);
      if (availRes.success) {
        for (const m of availRes.data || []) {
          if (m?.id != null) byId.set(String(m.id), m);
        }
      }
    }

    return Array.from(byId.values());
  }

  /**
   * Garage-style dual action:
   * 1) Validate customer city against assignment cities
   * 2) Auto-search mechanics
   * 3) Register complaint + assign selected (or first) mechanic
   */
  static async registerComplaintAndAssign({
    cityInput,
    customerName,
    phoneNumber,
    vehicleNumber = '',
    serviceType = 'roadside_assistance',
    serviceTag = '',
    problemDescription = '',
    address = '',
    pincode = '',
    locationLink = '',
    mechanicId = null,
    assignmentRemark = 'Auto-assigned via Assignment City',
  }) {
    const cities = await this.listActive();
    const matched = this.matchCity(cityInput, cities);
    if (!matched) {
      return {
        success: false,
        error: `City/district "${cityInput}" is not in Auto Mechanic Assignment cities. Add it first.`,
      };
    }

    const mechanics = await this.searchMechanicsForCity(matched, serviceTag || serviceType);
    if (!mechanics.length) {
      return {
        success: false,
        error: `No mechanic found for ${matched.name}. Add mechanics with this city/area or pick another city.`,
        matchedCity: matched,
        mechanics: [],
      };
    }

    let chosen = mechanics[0];
    if (mechanicId) {
      chosen = mechanics.find((m) => String(m.id) === String(mechanicId)) || chosen;
    }

    const user = getCurrentUser();
    const createRes = await ServiceAdvisorService.createServiceRequest(
      {
        customer_name: customerName,
        vehicle_number: vehicleNumber || 'NA',
        service_type: serviceType || 'roadside_assistance',
        priority: 'medium',
        contact_number: phoneNumber,
        alternate_number: '',
        problem: problemDescription || `Auto RSA — ${matched.name}`,
        address: address || `${matched.name}${matched.district ? `, ${matched.district}` : ''}`,
        pincode: pincode || '',
        customer_quoted_amount: 0,
        advance_payment: '',
        service_tag: serviceTag || matched.name,
        location_link: locationLink || '',
        media_upload: [],
        source: 'admin_assignment_city',
        remark: `Assignment city: ${matched.name}`,
        is_premium: false,
        card_number: '',
      },
      user?.id || '',
      user?.name || user?.email || 'Admin',
    );

    if (!createRes.success || !createRes.requestId) {
      return {
        success: false,
        error: createRes.message || 'Failed to register complaint',
        matchedCity: matched,
        mechanics,
      };
    }

    const mechanicName =
      chosen.mechanic_name || chosen.name || chosen.full_name || 'Mechanic';
    const mechanicContact = chosen.number || chosen.phone || chosen.contact || '';

    const assignRes = await ServiceAdvisorService.assignMechanicToRequest(
      createRes.requestId,
      chosen.id,
      mechanicName,
      mechanicContact,
      null,
      null,
      assignmentRemark,
    );

    if (!assignRes.success) {
      return {
        success: false,
        error: `Complaint #${createRes.requestId} created but assign failed: ${assignRes.message || assignRes.error}`,
        requestId: createRes.requestId,
        matchedCity: matched,
        mechanics,
        mechanic: chosen,
      };
    }

    const bellSeconds = clampBellSeconds(matched.bell_seconds ?? 1800);
    try {
      await this.stampPartnerBellOnRequest(createRes.requestId, bellSeconds);
    } catch (e) {
      console.warn('partner bell stamp failed', e);
    }

    return {
      success: true,
      requestId: createRes.requestId,
      matchedCity: matched,
      mechanic: chosen,
      mechanics,
      bellSeconds,
      message: `Complaint #${createRes.requestId} registered & assigned to ${mechanicName} · bell ${bellSeconds}s`,
    };
  }
}
